import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigateTransition } from '../lib/navigation';
import LoadingView from '../components/LoadingView';
import ReportCard from '../components/ReportCard';
import { useFlow } from '../state/flow';
import { rasterizeReportCard } from '../lib/quizShare';
import { QUIZ_COPY } from '../lib/quizCopy';

// Snappier than the ID-card loader — there's only one card to compose.
const MIN_MS = 3500;
const MAX_MS = 10000;

/** "Gathering your results" screen between the last question and the report
 * card. Rasterizes the shareable PNG while the trivia plays, then hands it to
 * the results screen via route state (which regenerates it if missing). */
export default function QuizLoading() {
  const { episode } = useParams();
  const ep = Number(episode);
  const navigate = useNavigateTransition();
  const { i18n } = useTranslation();
  const { quizResults } = useFlow();
  const result = quizResults[ep];
  const cardRef = useRef<HTMLDivElement | null>(null);

  // English-only quiz: reuse the EN trivia regardless of the app locale.
  const facts = useMemo(
    () => i18n.getFixedT('en')('loading.facts', { returnObjects: true }) as string[],
    [i18n],
  );

  const [cardReady, setCardReady] = useState(false);
  const [png, setPng] = useState<string | null>(null);
  const [composeDone, setComposeDone] = useState(false);
  const [startTime] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setTimeout(() => setCardReady(true), 300);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!cardReady || !result) return;
    let alive = true;
    void (async () => {
      try {
        if (cardRef.current) {
          const data = await rasterizeReportCard(cardRef.current);
          if (alive) setPng(data);
        }
      } catch {
        // Results screen regenerates the PNG as a fallback.
      } finally {
        if (alive) setComposeDone(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cardReady, result]);

  useEffect(() => {
    if (!result) return;
    const elapsed = Date.now() - startTime;
    const goNow = () => navigate(`/class/${ep}/results`, png ? { state: { png } } : undefined);
    if (composeDone && elapsed >= MIN_MS) {
      goNow();
      return;
    }
    const target = composeDone ? MIN_MS : MAX_MS;
    const id = window.setTimeout(goNow, target - elapsed);
    return () => window.clearTimeout(id);
  }, [composeDone, png, navigate, ep, startTime, result]);

  if (!result) return <Navigate to="/class" replace />;

  return (
    <LoadingView title={QUIZ_COPY.loadingTitle} subtitle={QUIZ_COPY.loadingSubtitle} facts={facts}>
      {cardReady && (
        <div className="loading__offscreen" aria-hidden="true">
          <ReportCard ref={cardRef} result={result} variant="full" />
        </div>
      )}
    </LoadingView>
  );
}
